import React, { useEffect, useState } from 'react';
import { X, Mail } from 'lucide-react';

/**
 * Verification code modal.
 * Props:
 *  - open: boolean
 *  - email: string (already-sent-to email)
 *  - firstName: string | null
 *  - purpose: 'booking' | 'subscribe'
 *  - onCancel: () => void
 *  - onSubmit: (code: string) => Promise<void>  // parent handles final submission
 *  - onResend: () => Promise<void>              // parent re-triggers /verify/send
 *  - submitting: boolean
 *  - error: string | null                       // set by parent when code fails
 */
export default function VerifyModal({
  open,
  email,
  firstName,
  purpose,
  onCancel,
  onSubmit,
  onResend,
  submitting,
  error,
}) {
  const [code, setCode] = useState('');
  const [resendCooldown, setResendCooldown] = useState(45);
  const [resending, setResending] = useState(false);

  // Reset each time modal opens
  useEffect(() => {
    if (open) {
      setCode('');
      setResendCooldown(45);
    }
  }, [open]);

  // Cooldown countdown
  useEffect(() => {
    if (!open || resendCooldown <= 0) return undefined;
    const t = setInterval(() => setResendCooldown((c) => Math.max(0, c - 1)), 1000);
    return () => clearInterval(t);
  }, [open, resendCooldown]);

  if (!open) return null;

  const purposeLabel =
    purpose === 'booking' ? 'complete your inquiry' : 'confirm your spot on the list';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (code.length !== 6) return;
    await onSubmit(code);
  };

  const handleResend = async () => {
    if (resendCooldown > 0 || resending) return;
    setResending(true);
    try {
      await onResend();
      setResendCooldown(45);
    } finally {
      setResending(false);
    }
  };

  return (
    <div
      data-testid="verify-modal"
      className="fixed inset-0 z-[120] flex items-center justify-center px-4 py-8"
      role="dialog"
      aria-modal="true"
    >
      <div
        className="absolute inset-0 bg-zb-ink/70 backdrop-blur-sm"
        onClick={submitting ? undefined : onCancel}
      />
      <div className="relative w-full max-w-md bg-zb-bg border border-zb-border p-8 md:p-10 shadow-2xl zb-fade-up">
        <button
          type="button"
          onClick={onCancel}
          data-testid="verify-modal-close"
          className="absolute top-4 right-4 text-zb-ink2 hover:text-zb-ink transition-colors"
          aria-label="Close"
          disabled={submitting}
        >
          <X size={20} />
        </button>

        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 rounded-full bg-zb-brand/10 flex items-center justify-center text-zb-brand">
            <Mail size={18} />
          </div>
          <div className="font-label text-zb-accent text-[10.5px] tracking-[0.28em]">
            One quick step
          </div>
        </div>

        <h2 className="font-serif text-3xl md:text-4xl text-zb-ink leading-tight">
          Check your <span className="italic text-zb-brand">email.</span>
        </h2>
        <p className="mt-4 text-zb-ink2 text-base leading-relaxed">
          I&rsquo;ve just sent a 6-digit code to{' '}
          <span className="text-zb-ink font-medium">{email}</span>. Enter it
          below to {purposeLabel}.
        </p>
        <p className="mt-2 text-zb-accent text-sm italic">
          Please check your spam / junk folder if it hasn&rsquo;t arrived within a minute.
        </p>

        <form onSubmit={handleSubmit} className="mt-6">
          <label
            htmlFor="verify-code"
            className="font-label text-zb-accent block mb-2 text-[10.5px] tracking-[0.28em]"
          >
            Verification code
          </label>
          <input
            id="verify-code"
            data-testid="verify-code-input"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            placeholder="_ _ _ _ _ _"
            className="w-full bg-transparent border-2 border-zb-border focus:border-zb-brand text-zb-ink font-serif text-3xl tracking-[0.5em] text-center py-4 outline-none transition-colors"
            autoFocus
          />

          {error && (
            <p
              data-testid="verify-error"
              className="mt-3 text-sm text-zb-brand"
            >
              {error}
            </p>
          )}

          <div className="mt-6 flex flex-col gap-3">
            <button
              type="submit"
              disabled={code.length !== 6 || submitting}
              data-testid="verify-submit"
              className="w-full rounded-full bg-zb-brand hover:bg-zb-brandHover disabled:opacity-50 text-zb-bg px-8 py-3.5 text-xs tracking-[0.22em] uppercase transition-colors"
            >
              {submitting ? 'Confirming\u2026' : 'Confirm & submit'}
            </button>
            <button
              type="button"
              onClick={handleResend}
              disabled={resendCooldown > 0 || resending}
              data-testid="verify-resend"
              className="text-zb-ink2 hover:text-zb-brand disabled:opacity-50 text-xs tracking-[0.22em] uppercase transition-colors underline underline-offset-4"
            >
              {resendCooldown > 0
                ? `Resend code (${resendCooldown}s)`
                : resending
                ? 'Sending\u2026'
                : 'Resend code'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
